import { BoxGeometry, BufferGeometry, Float32BufferAttribute, ShapeUtils, Vector2, Vector3 } from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

import {
    type GeometryModel,
    GeometryModelGeometryType,
    type LayerModel,
    LayerModelLayerType,
    type PointModel,
    type PrefabModel
} from 'api/models';
import { getLayerColor } from 'app/modules/wallforming/components/scenes/prefab-layer-renderer/utils/layerColorMap';

import type { DevToolsSliceState } from 'app/modules/wallforming/components/state-management/placement/states/devToolsSliceState';

export type OverlayRenderMode = 'mesh' | 'line';

export interface OverlayItem {
    id: string;
    layerType: LayerModelLayerType;
    geometry: BufferGeometry;
    color: string;
    opacity: number;
    renderMode: OverlayRenderMode;
    /** Faces are rendered double-sided and slightly transparent */
    doubleSide: boolean;
}

// ─── Geometry builders ───────────────────────────────────────────────────────

function buildPointGeometry(pts: PointModel[], size = 0.08): BufferGeometry | null {
    if (pts.length === 0) {
        return null;
    }

    const geos = pts.map((p) => {
        const g = new BoxGeometry(size, size, size);
        g.translate(p.x, p.y, p.z);
        return g;
    });

    if (geos.length === 1) {
        return geos[0];
    }

    const merged = mergeGeometries(geos, false);
    geos.forEach((g) => g.dispose());
    return merged;
}

function buildLineGeometry(pts: PointModel[]): BufferGeometry | null {
    if (pts.length < 2) {
        return null;
    }

    const positions = new Float32Array(pts.length * 3);
    pts.forEach((p, i) => {
        positions[i * 3] = p.x;
        positions[i * 3 + 1] = p.y;
        positions[i * 3 + 2] = p.z;
    });

    const geo = new BufferGeometry();
    geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
    return geo;
}

function buildFaceGeometry(pts: PointModel[]): BufferGeometry | null {
    if (pts.length < 3) {
        return null;
    }

    const v3d = pts.map((p) => new Vector3(p.x, p.y, p.z));

    // Build tangent basis from first two edges (same technique as GLB generator)
    const origin = v3d[0];
    const e1 = v3d[1].clone().sub(origin);
    const e2 = v3d[2].clone().sub(origin);
    const normal = new Vector3().crossVectors(e1, e2).normalize();
    const u = e1.clone().normalize();
    const v = new Vector3().crossVectors(normal, u).normalize();

    // Project all points onto the local 2D plane
    const uv: Vector2[] = v3d.map((p) => {
        const rel = p.clone().sub(origin);
        return new Vector2(rel.dot(u), rel.dot(v));
    });

    let triangles: number[][];
    try {
        triangles = ShapeUtils.triangulateShape(uv, []);
    } catch {
        return null;
    }

    if (!triangles || triangles.length === 0) {
        return null;
    }

    const positions: number[] = [];
    for (const [a, b, c] of triangles) {
        const p0 = v3d[a],
            p1 = v3d[b],
            p2 = v3d[c];
        positions.push(p0.x, p0.y, p0.z, p1.x, p1.y, p1.z, p2.x, p2.y, p2.z);
    }

    const geo = new BufferGeometry();
    geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geo.computeVertexNormals();
    return geo;
}

function buildCircleFromPoints(pts: PointModel[]): BufferGeometry | null {
    // The API sends the circle's control points (typically 3: center + 2 on circumference
    // or a series of approximation points). Use what's available as a polyline fallback.
    return buildLineGeometry(pts);
}

function buildGeometryForModel(
    geoModel: GeometryModel
): { geometry: BufferGeometry; renderMode: OverlayRenderMode; opacity: number; doubleSide: boolean } | null {
    const { geometryType, points } = geoModel;

    switch (geometryType) {
        case GeometryModelGeometryType.Point: {
            const geo = buildPointGeometry(points);
            return geo ? { geometry: geo, renderMode: 'mesh', opacity: 1.0, doubleSide: false } : null;
        }

        case GeometryModelGeometryType.Line:
        case GeometryModelGeometryType.Polyline:
        case GeometryModelGeometryType.Arc: {
            const geo = buildLineGeometry(points);
            return geo ? { geometry: geo, renderMode: 'line', opacity: 1.0, doubleSide: false } : null;
        }

        case GeometryModelGeometryType.Circle: {
            const geo = buildCircleFromPoints(points);
            return geo ? { geometry: geo, renderMode: 'line', opacity: 1.0, doubleSide: false } : null;
        }

        case GeometryModelGeometryType.Face: {
            const geo = buildFaceGeometry(points);
            return geo ? { geometry: geo, renderMode: 'mesh', opacity: 0.55, doubleSide: true } : null;
        }

        case GeometryModelGeometryType.Hatch: {
            const geo = buildFaceGeometry(points);
            return geo ? { geometry: geo, renderMode: 'mesh', opacity: 0.35, doubleSide: true } : null;
        }

        // Text / AttDef / None: skip — not visually meaningful
        default:
            return null;
    }
}

function traversePrefabs(prefab: PrefabModel, cb: (node: PrefabModel, path: string) => void, path = prefab.name): void {
    cb(prefab, path);

    prefab.children.forEach((child, idx) => {
        traversePrefabs(child, cb, `${path}/${child.name || `child-${idx}`}`);
    });
}

function getAllLayers(prefab: PrefabModel): LayerModel[] {
    return [...prefab.graphicLayers, ...prefab.logicLayers];
}

export interface PrefabTreeNode {
    id: string;
    name: string;
    layers: LayerModel[];
    children: PrefabTreeNode[];
}

export function buildPrefabTree(prefab: PrefabModel, path = prefab.name): PrefabTreeNode {
    return {
        id: path,
        name: prefab.name,
        layers: getAllLayers(prefab),
        children: prefab.children.map((child, idx) => buildPrefabTree(child, `${path}/${child.name || `child-${idx}`}`))
    };
}

export function buildPartLayerVisibilityKey(partPath: string, layerType: LayerModelLayerType): string {
    return `${partPath}::${layerType}`;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Returns a flat list of renderable overlay items for a given prefab.
 *
 * - Layers with `layerVisibility[type] === false` are skipped.
 * - Missing entries default to visible.
 * - Includes the full prefab tree (representation + subparts).
 */
export function buildPrefabOverlayItems(
    prefab: PrefabModel,
    layerVisibility: DevToolsSliceState['layerVisibility'],
    partLayerVisibility?: DevToolsSliceState['partLayerVisibility']
): OverlayItem[] {
    const items: OverlayItem[] = [];

    traversePrefabs(prefab, (node, path) => {
        for (const [layerIndex, layer] of getAllLayers(node).entries()) {
            const scopedLayerKey = buildPartLayerVisibilityKey(path, layer.layerType);
            const isLayerVisible =
                partLayerVisibility && scopedLayerKey in partLayerVisibility
                    ? partLayerVisibility[scopedLayerKey] !== false
                    : layerVisibility[layer.layerType] !== false;

            if (!isLayerVisible) {
                continue;
            }

            const color = getLayerColor(layer.layerType);

            layer.geometries.forEach((geoModel, gIdx) => {
                const result = buildGeometryForModel(geoModel);
                if (!result) {
                    return;
                }

                items.push({
                    id: `${path}__${layer.layerType}__${layerIndex}__${gIdx}`,
                    layerType: layer.layerType,
                    geometry: result.geometry,
                    color,
                    opacity: result.opacity,
                    renderMode: result.renderMode,
                    doubleSide: result.doubleSide
                });
            });
        }
    });

    return items;
}

/**
 * Returns all unique layer types present in a prefab tree.
 * Useful for building the layer toggle list.
 */
export function getPrefabLayerTypes(prefab: PrefabModel): LayerModelLayerType[] {
    const set = new Set<LayerModelLayerType>();
    traversePrefabs(prefab, (node) => {
        getAllLayers(node).forEach((layer) => set.add(layer.layerType));
    });
    return [...set];
}

/**
 * Counts geometries per layer type in a prefab tree.
 */
export function getPrefabLayerGeometryCounts(prefab: PrefabModel): Map<LayerModelLayerType, number> {
    const counts = new Map<LayerModelLayerType, number>();
    traversePrefabs(prefab, (node) => {
        for (const layer of getAllLayers(node)) {
            counts.set(layer.layerType, (counts.get(layer.layerType) ?? 0) + layer.geometries.length);
        }
    });
    return counts;
}
